import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { ConsigneeGateway } from 'src/app/application/gateway/ConsigneeGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { CityService } from 'src/app/application/service/CityService';
import { City } from 'src/app/domain/models/City';
import { Consignee } from 'src/app/domain/models/Consignee';
import { Country } from 'src/app/domain/models/Country';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { formLimit } from 'src/app/ui/util/form-limit';

@Component({
  selector: 'app-consignee-form',
  templateUrl: './consignee-form.component.html',
  styleUrls: ['./consignee-form.component.css'],

})
export class ConsigneeFormComponent implements OnInit {
  title: string = ''
  countries: Country[] = []
  cities: City[] = []
  selectedCountry: Country | undefined
  submit = false
  loading = false
  consigneeId = 0
  consignee: Consignee | undefined

  profileUser: BasicUser | undefined

  breadcrumbMenuItems: MenuItem[] = [{ label: '' }]
  breadcrumbMenuItems2: MenuItem[] = [{ label: '' }]
  breadcrumbMenuItems2Edit: MenuItem[] = [{ label: '' }]
  breadcrumbHome: MenuItem = { label: ''}

  formLimit = formLimit

  formGroup: FormGroup = this._formBuilder.group({
    name: ['', Validators.required],
    vat: [null],
    contactCountry: ['', Validators.required],
    contactCity: [''],
    city: [''],
    contactName: ['', Validators.required],
    contactAddress: ['', Validators.required],
    contactPhone: [''],
    contactEmail: ['', [Validators.required, Validators.email]],
  })

  constructor(private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _messageService: MessageService,
    private _authService: AuthGateway,
    private _countryService: CountryGateway,
    private _consigneeService: ConsigneeGateway,
    public translate: TranslateService,
    private _cityService: CityService) {

  }

  ngOnInit(): void {

    this.profileUser = this._authService.getUser()

    let breadcrumbStep1ItemLabel1 = 'Consignee'
    let breadcrumbStep1ItemLabel2 = 'Create consignee'
    let breadcrumbStep1ItemLabel2Edit = 'Edit consignee'
    let homeItemLabel = 'User panel'

    if(this.profileUser) {
      switch(this.profileUser.lang) {
        case 'en':
          breadcrumbStep1ItemLabel1 = 'Consignee'
          breadcrumbStep1ItemLabel2 = 'Create consignee'
          breadcrumbStep1ItemLabel2Edit = 'Edit consignee'
          homeItemLabel = 'User panel'
          break
        case 'es':
          breadcrumbStep1ItemLabel1 = 'Consignee'
          breadcrumbStep1ItemLabel2 = 'Crear consignee'
          breadcrumbStep1ItemLabel2Edit = 'Editar consignee'
          homeItemLabel = 'Panel de usuario'
          break
      }
    }

    this.breadcrumbMenuItems = [{ label: breadcrumbStep1ItemLabel1, disabled: true }]
    this.breadcrumbMenuItems2 = [
      { label: breadcrumbStep1ItemLabel1, routerLink: ['/' + Routes.userConsignees]},
      { label: breadcrumbStep1ItemLabel2, disabled: true }
    ]
    this.breadcrumbMenuItems2Edit = [
      { label: breadcrumbStep1ItemLabel1, routerLink: ['/' + Routes.userConsignees]},
      { label: breadcrumbStep1ItemLabel2Edit, disabled: true }
    ]
    this.breadcrumbHome = { label: homeItemLabel, routerLink: '/' + Routes.userPanelPage}


    this._activatedRoute.params.subscribe(params => {
      this.consigneeId = params['consigneeId']
    })

    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Country.ManyRetrieveError') });
        console.log(e)
      },
      complete: () => console.info('Country query successfully')
    })

    if(this.consigneeId && this.consigneeId > 0) {
      this.title = this.translate.instant('UserPages.UserConsignees.ConsigneeForm.EditTitle')
      this._consigneeService.getById(this.consigneeId).subscribe({
        next: (v) => {
          this.consignee = v
          this.loadForm()
        },
        error: (e) => {
          console.log(e)
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Consignee.SingleRetrieveError') });
        },
        complete: () => console.info('Consignee query successfully')
      })
    }
    else {
      this.title = this.translate.instant('UserPages.UserConsignees.ConsigneeForm.CreateTitle')
      this.consignee = {}
    }
  }

  loadForm() {
    if(this.consignee) {
      if(this.consignee.contactCountry){
        this.loadCities(this.consignee.contactCountry.id)
      }
      this.formGroup.controls["name"].setValue(this.consignee.companyName)
      this.formGroup.controls["vat"].setValue(this.consignee.vat)
      this.formGroup.controls["contactName"].setValue(this.consignee.contactName)
      this.formGroup.controls["contactAddress"].setValue(this.consignee.contactAddress)
      this.formGroup.controls["contactEmail"].setValue(this.consignee.contactEmail)
      this.formGroup.controls["contactPhone"].setValue(this.consignee.contactPhone)
      this.formGroup.controls["contactCity"].setValue(this.consignee.contactCity)
      this.formGroup.controls["contactCountry"].setValue(this.consignee.contactCountry)
      this.formGroup.controls["city"].setValue(this.consignee.city)
      this.selectedCountry = this.countries.find(x => x.id == this.consignee?.contactCountry?.id)
    }
  }

  save() {
    this.submit = true
    this.loading = true
    if(this.formGroup.valid && this.consignee) {
      this.consignee.companyName = this.formGroup.controls["name"].value
      this.consignee.vat = this.formGroup.controls["vat"].value
      this.consignee.contactName = this.formGroup.controls["contactName"].value
      this.consignee.contactAddress = this.formGroup.controls["contactAddress"].value
      this.consignee.contactEmail = this.formGroup.controls["contactEmail"].value
      this.consignee.contactPhone = this.formGroup.controls["contactPhone"].value
      this.consignee.contactCity = this.formGroup.controls["contactCity"].value
      if(this.formGroup.controls["city"].value){
        this.consignee.cityId = this.formGroup.controls["city"].value.id
      }else{
        this.consignee.cityId = undefined
      }
      this.consignee.contactCountryId = this.formGroup.controls["contactCountry"].value.id

      if(this.consigneeId && this.consigneeId > 0) {
        //update
        this._consigneeService.update(this.consignee).subscribe({
          next: (v) => {
            this.consignee = v
            this._messageService.add({ severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.Consignee.EditSuccess') })
            this.loading = false
            this.submit = false
          },
          error: (e) => {
            this.loading = false
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.Consignee.EditError') });
          },
          complete: () =>  {
            this.loading = false
            this.submit = false
            console.info('Consignee update successfully') }
        })
      }
      else {
        //create
        this.consignee.username = this._authService.getUsername()
        this._consigneeService.create(this.consignee).subscribe({
          next: (v) => {
            this.consignee = v
            this.consigneeId = this.consignee.id ? this.consignee.id : 0
            this.title = this.translate.instant('UserPages.UserConsignees.ConsigneeForm.EditTitle')
            this._messageService.add({ severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.Consignee.CreateSuccess') })
            this.loading = false
            this.submit = false
          },
          error: (e) => {
            this.loading = false
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.Consignee.CreateError') });
          },
          complete: () =>  {
            this.loading = false
            this.submit = false
            console.info('Consignee created successfully')
          }
        })
      }
    }
    else {
      this.loading = false
    }
  }

  cancel() {
    this._router.navigate([Routes.userConsignees])
  }

  getErrorMessage(fieldName: string): string {
    if (this.formGroup.controls[fieldName].hasError('required')) {
      return this.translate.instant('FormErrorMessage.FieldValueRequired')
    }
    if (this.formGroup.controls[fieldName].hasError('email')) {
      return this.translate.instant('FormErrorMessage.EmailFieldEmailFormatError')
    }
    return ''
  }

  onChangeCountry() {
    this.selectedCountry = this.countries.find(x => x.id == this.formGroup.controls['contactCountry'].value.id)

    this.formGroup.patchValue({
      city: null,
      contactCity: null
    });

    if(this.selectedCountry) {
      this.loadCities(this.selectedCountry.id)
    }
  }

  loadCities(countryId : any){
      this._cityService.getByCountry(countryId).subscribe({
        next: (v) => {
          this.cities = v;

          if (this.cities && this.cities.length > 0) {
            this.formGroup.get('city')?.setValidators([Validators.required]);
            this.formGroup.get('contactCity')?.clearValidators();
          } else {
            this.formGroup.get('contactCity')?.setValidators([Validators.required]);
            this.formGroup.get('city')?.clearValidators();
          }
          this.formGroup.get('city')?.updateValueAndValidity();
          this.formGroup.get('contactCity')?.updateValueAndValidity();
          },
        error: (e) => {
          this.cities = [];
          this.loading = false;
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.City.ManyRetrieveError') })
        },
        complete: () => console.info('Cities query successfully')
      })

  }
}
